import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {map, catchError, switchMap, withLatestFrom} from 'rxjs/operators';
import {iif, of} from 'rxjs';
import { ProductBase as Entity } from './product-base.model';
import {AppStoreEntityService} from "../app-store-entity.service";
import { ProductBaseActions as EntityActions} from './product-base.actions'
import {ProductBaseSelectors as Selectors} from "./product-base.selectors";
import {ProductBaseService} from "./product-base.service";
import {IResultWithPageInfo} from "../app-store.interfaces";
import {select, Store} from "@ngrx/store";
import {AppState} from "../app-store.state";

@Injectable()
export class ProductBaseEffects {
  loadAndSelectIfNecessary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.loadAndSelectIfNecessary),
      withLatestFrom(this.store.pipe(select(Selectors.selectAll))),
      switchMap(([action, entities]) => {
        const existingProductBase = entities.find(x => x.id === action.productBaseId);
        if (existingProductBase !== undefined) {
          return of(EntityActions.loadAndSelectSuccess({entity: existingProductBase, correlationId: action.correlationId}))
        }

        return iif(() => action.productCategoryId !== undefined,
          this.productBaseService.loadSingleProductBase<Entity>(this.EntityInfo, action.productBaseId, Number(action.productCategoryId)).pipe(
            map((entity: Entity) =>
              EntityActions.loadAndSelectSuccess({entity, correlationId :action.correlationId})
            ),
            catchError((error) =>
              of(EntityActions.loadAndSelectFailure({error,correlationId: action.correlationId}))
            )
          ),
          this.appStoreEntityService.loadSingle<Entity>(this.EntityInfo, action.productBaseId).pipe(
            map((entity: Entity) =>
              EntityActions.loadAndSelectSuccess({entity, correlationId :action.correlationId})
            ),
            catchError((error) =>
              of(EntityActions.loadAndSelectFailure({error,correlationId: action.correlationId}))
            )
          )
        )
      })
    )
  );

  clone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.clone),
      switchMap(({entity, correlationId}) =>
        this.productBaseService.clone(entity.id, true).pipe(
          map((entity: Entity) =>
            EntityActions.cloneSuccess({entity, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.cloneFailure({error, correlationId}))
          )
        )
      )
    ));

  loadPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.loadPage),
      switchMap(({page, criteria, correlationId}) =>
        this.appStoreEntityService.loadPage<Entity>(this.EntityInfo, page, criteria).pipe(
          map((resultWithPageInfo: IResultWithPageInfo<Entity>) =>
            EntityActions.loadPageSuccess({resultWithPageInfo, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.loadPageFailure({error, correlationId}))
          )
        )
      )
    )
  );

  loadProductBasePageForEditor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.loadProductBasePageForEditor),
      switchMap(({page, criteria, correlationId}) =>
        this.appStoreEntityService.loadPage<Entity>(this.EntityInfo, page, criteria).pipe(
          map((resultWithPageInfo: IResultWithPageInfo<Entity>) =>
            EntityActions.loadProductBasePageForEditorSuccess({resultWithPageInfo, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.loadPageFailure({error, correlationId}))
          )
        )
      )
    )
  );

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.load),
      switchMap(({criteria, correlationId}) =>
        this.appStoreEntityService.load<Entity>(this.EntityInfo, criteria).pipe(
          map((entities: Entity[]) =>
            EntityActions.loadSuccess({entities, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.loadFailure({error, correlationId}))
          )
        )
      )
    )
  );

  loadListPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.loadListPage),
      switchMap(({page, criteria, correlationId}) =>
        this.appStoreEntityService.loadPage<Entity>(this.EntityInfo, page, criteria).pipe(
          map((resultWithPageInfo: IResultWithPageInfo<Entity>) =>
            EntityActions.loadListPageSuccess({resultWithPageInfo, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.loadPageFailure({error, correlationId}))
          )
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.create),
      switchMap(({entity, correlationId}) =>
        this.appStoreEntityService.create<Entity>(this.EntityInfo, entity).pipe(
          map((result: Entity) =>
            EntityActions.createSuccess({entity: result, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.createFailure({error, correlationId}))
          )
        )
      )
    )
  );

  createMany = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.createMany),
      switchMap(({entities, correlationId}) =>
        this.appStoreEntityService.createMany<Entity[]>(this.EntityInfo, entities).pipe(
          map((result: Entity[]) =>
            EntityActions.createManySuccess({entities: result, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.createManyFailure({error, correlationId}))
          )
        )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.update),
      switchMap(({entity, correlationId}) =>
        this.appStoreEntityService.update(this.EntityInfo, entity).pipe(
          map((result: Entity) =>
            EntityActions.updateSuccess({entity: result, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.updateFailure({error, correlationId}))
          )
        )
      )
    )
  );

  updateRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.updateRequest),
      switchMap(({entity, correlationId}) =>
        this.productBaseService.updateRequest(entity).pipe(
          map((result: Entity) =>
            EntityActions.updateSuccess({entity: result, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.updateFailure({error, correlationId}))
          )
        )
      )
    )
  );

  updateMany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.updateMany),
      switchMap(({entities, correlationId}) =>
        this.appStoreEntityService.updateMany(this.EntityInfo, entities).pipe(
          map((results: Entity[]) =>
            EntityActions.updateManySuccess({entities: results, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.updateManyFailure({error, correlationId}))
          )
        )
      )
    )
  );

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.remove),
      switchMap(({entityId, correlationId}) =>
        this.appStoreEntityService.remove<Entity>(this.EntityInfo, entityId).pipe(
          map((entity) =>
            EntityActions.removeSuccess({entity, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.removeFailure({error, correlationId}))
          )
        )
      )
    )
  );

  getTotalProductCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.getTotalProductCount),
      switchMap(() =>
        this.productBaseService.getTotalCount().pipe(
          map((count) => EntityActions.updateTotalProductCount({ count })),
          catchError((error) => of(EntityActions.getTotalProductCountFailed({ error })))
        )
      )
    )
  );

  EntityInfo = {modelName: "product-base"};

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private appStoreEntityService: AppStoreEntityService,
    private productBaseService: ProductBaseService
  ) {
  }
}
